import type { ChartType } from 'tio-common/types/chart';
import type { DateGroupingInterval } from './date-grouping';

// Unified configuration interface for both chart and table reports
export interface UnifiedReportConfig {
  // View state
  viewType: 'table' | 'chart';

  // Chart-specific settings (when viewType = 'chart')
  chartSettings?: {
    chartType: ChartType;
    groupByField: string;
    dateGrouping?: DateGroupingInterval;
    showTableView?: boolean;
  };

  // Table-specific settings (when viewType = 'table')
  tableSettings?: {
    showIndividualApplications: boolean;
    selectedColumns?: string[];
    columnOrder?: string[];
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  };

  // Shared filter state (works for both views)
  filters: {
    // Program filters
    programTemplateId?: string;
    programCreatedAtStart?: string;
    programCreatedAtEnd?: string;
    excludeNonVoucherPrograms?: boolean;
    courseCashProgramsOnly?: boolean;

    // Application filters
    applicationStates?: string[];
    applicationCreatedAtStart?: string;
    applicationCreatedAtEnd?: string;
    applicationDateRangePreset?: string; // Relative date preset ID
    inCollections?: boolean;

    // Payment filters
    includePayments?: boolean;
    paymentStates?: string[];
    paymentYear?: string;

    // Course filters
    includeCourses?: boolean;
    coursesBeginDate?: string;
    coursesEndDate?: string;

    // Employee filters
    employeeId?: string;
    terminatedEmployees?: boolean;
    excludeTermVerified?: boolean;
    approverId?: string;

    // General filters
    timeRange?: string;
    searchText?: string;
  };

  exportSettings?: {
    format: 'csv' | 'excel' | 'pdf';
    includeCharts?: boolean;
    dateRange?: string;
  };
}

/**
 * Configuration manager for unified reporting
 */
export class ReportingConfigManager {
  /**
   * Parse URL search params into unified config
   */
  static fromUrlParams(params: URLSearchParams): UnifiedReportConfig {
    const viewType = (params.get('viewType') as 'table' | 'chart') || 'table';

    const config: UnifiedReportConfig = {
      viewType,
      filters: {},
    };

    // Chart settings
    if (viewType === 'chart') {
      config.chartSettings = {
        chartType: (params.get('chartType') as ChartType) || 'bar',
        groupByField: params.get('groupByField') || 'application_state',
        dateGrouping: (params.get('dateGrouping') as DateGroupingInterval) || 'day',
        showTableView: params.get('showTableView') === 'true',
      };
    }

    // Table settings
    if (viewType === 'table') {
      config.tableSettings = {
        showIndividualApplications: params.get('showIndividualApplications') === 'true',
      };
    }

    const filterKeys = [
      'programTemplateId',
      'programCreatedAtStart',
      'programCreatedAtEnd',
      'excludeNonVoucherPrograms',
      'courseCashProgramsOnly',
      'applicationCreatedAtStart',
      'applicationCreatedAtEnd',
      'applicationDateRangePreset',
      'inCollections',
      'includePayments',
      'paymentYear',
      'includeCourses',
      'coursesBeginDate',
      'coursesEndDate',
      'employeeId',
      'terminatedEmployees',
      'excludeTermVerified',
      'approverId',
      'timeRange',
      'searchText',
    ];

    filterKeys.forEach((key) => {
      const value = params.get(key);
      if (value !== null) {
        if (
          key === 'excludeNonVoucherPrograms' ||
          key === 'courseCashProgramsOnly' ||
          key === 'inCollections' ||
          key === 'includePayments' ||
          key === 'includeCourses' ||
          key === 'terminatedEmployees' ||
          key === 'excludeTermVerified'
        ) {
          (config.filters as Record<string, unknown>)[key] = value === 'true';
        } else {
          (config.filters as Record<string, unknown>)[key] = value;
        }
      }
    });

    const arrayFilterKeys = ['applicationStates', 'paymentStates'];
    arrayFilterKeys.forEach((key) => {
      const value = params.get(key);
      if (value) {
        (config.filters as Record<string, unknown>)[key] = value.split(',').filter(Boolean);
      }
    });

    return config;
  }
}
